import { MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { Paths } from "common/paths/paths";
import { getString } from "common/uistringlabels/uiStringUtils";
import { AIForMeTool, ApprovalStatus } from "lib/models/aiforme-models";
import { CatalogicalService, UserPermissions } from "lib/services";
import { GetItemResponse, submitError } from "lib/services/catalogical-models";
import { CSRFService } from "lib/services/csrf-service";

import { AiForMeFormData, YesNo } from "../constants/platform-constants";

export interface ToolSubmissionCallbacks {
  addErrorBanner: (message: string, cti?: boolean, error?: any) => void;
  addMessageBanner: (message: string, type: MessageBannerType) => void;
  navigateToSuccess: () => void;
  setIsLoading: (loading: boolean) => void;
}

const HANDLER_DEBOUNCE_TIMEOUT = 300;
let voteTO: NodeJS.Timeout | undefined;

export const submitTool = async (
  formData: AiForMeFormData,
  callbacks: ToolSubmissionCallbacks,
  includeCustomBuiltDetails = false,
  includeThirdPartyDetails = false
): Promise<void> => {
  callbacks.setIsLoading(true);

  const client = new CatalogicalService<AIForMeTool>("aiforme");
  const username = UserPermissions.current.username;
  const csrfService = new CSRFService();

  const newTool: Partial<AIForMeTool> = {
    title: formData.title,
    type: "Tool",
    description: formData.description,
    directToolUrl: formData.directToolUrl,
    learnMoreLink: formData.directToolUrl,
    documentationUrl: formData.documentationUrl,
    categories: formData.selectedCategories,
    jobFamilies: formData.selectedJobFamilies,
    status: "PENDING" as ApprovalStatus,
    tags: [],
    creationDate: new Date().toISOString(),
    recommended: false,
    username: formData.username || username!,
    toolMaturity: formData.toolMaturity,
    dataClassification: formData.dataClassification,
    basePlatform: formData.basePlatform,
    toolBindle: formData.toolBindle,
  };

  // For tools with a recognized base platform (i.e. Cedric)
  if (formData.basePlatformMetadataTaxonomyId) {
    newTool.basePlatformMetadataTaxonomyId = formData.basePlatformMetadataTaxonomyId;
  }

  // Only include custom built details and otherPlatform when specified
  if (includeCustomBuiltDetails) {
    Object.assign(newTool, {
      otherPlatform: formData.platformName,
      securityReviewed: formData.securityReviewed?.toLowerCase() as YesNo,
      securityReviewLink: formData.securityReviewLink,
      securityReviewSystem: formData.securityReviewSystem,
      modelsUsed: formData.modelsUsed,
      otherModel: formData.otherModel,
      storesInputPrompts: formData.storesInputPrompts?.toLowerCase() as YesNo,
      collectsDataForRetraining: formData.collectsDataForRetraining?.toLowerCase() as YesNo,
      toolCTILink: formData.toolCTILink,
    });
  }

  // Include third-party specific details when specified
  if (includeThirdPartyDetails) {
    Object.assign(newTool, {
      otherPlatform: formData.platformName,
      vendorName: formData.vendorName,
      businessJustification: formData.businessJustification,
    });
  }

  if (voteTO) {
    clearTimeout(voteTO);
  }

  voteTO = setTimeout(async () => {
    try {
      const csrfToken = await csrfService.getToken();
      const response = await client.submitTool(newTool, csrfToken);

      if ("errorMessage" in response) {
        callbacks.addErrorBanner(response.errorMessage, response.cti ?? true, response.error);
      } else {
        callbacks.navigateToSuccess();
        callbacks.addMessageBanner(getString("aiForMe.submitToolBanner"), MessageBannerType.Success);
      }

      scrollTo({ top: 0 });
      callbacks.setIsLoading(false);
    } catch (reason: any) {
      callbacks.addErrorBanner(getString("aiForMe.failedTool2"));
      console.error(reason);
      callbacks.setIsLoading(false);
    }
  }, HANDLER_DEBOUNCE_TIMEOUT);
};

export const editTool = async (
  formData: AiForMeFormData,
  taxonomyId: string,
  callbacks: ToolSubmissionCallbacks,
  includeCustomBuiltDetails = false,
  includeThirdPartyDetails = false
): Promise<void> => {
  callbacks.setIsLoading(true);

  const client = new CatalogicalService<AIForMeTool>("aiforme");
  const username = UserPermissions.current.username;
  const csrfService = new CSRFService();

  const newTool: Partial<AIForMeTool> = {
    taxonomyId,
    title: formData.title,
    type: "Tool",
    description: formData.description,
    directToolUrl: formData.directToolUrl,
    learnMoreLink: formData.directToolUrl,
    documentationUrl: formData.documentationUrl,
    categories: formData.selectedCategories,
    jobFamilies: formData.selectedJobFamilies,
    status: (formData.status as ApprovalStatus) === "APPROVED" ? "UPDATED" : (formData.status as ApprovalStatus),
    creationDate: formData.creationDate,
    recommended: false,
    username: username!,
    toolMaturity: formData.toolMaturity,
    dataClassification: formData.dataClassification,
    basePlatform: formData.basePlatform,
    toolBindle: formData.toolBindle,
    tags: formData.tags,
  };

  if (formData.basePlatformMetadataTaxonomyId) {
    newTool.basePlatformMetadataTaxonomyId = formData.basePlatformMetadataTaxonomyId;
  }

  // Only include custom built details and otherPlatform when specified
  if (includeCustomBuiltDetails) {
    Object.assign(newTool, {
      otherPlatform: formData.platformName,
      securityReviewed: formData.securityReviewed?.toLowerCase() as YesNo,
      securityReviewLink: formData.securityReviewLink,
      securityReviewSystem: formData.securityReviewSystem,
      modelsUsed: formData.modelsUsed,
      otherModel: formData.otherModel,
      storesInputPrompts: formData.storesInputPrompts?.toLowerCase() as YesNo,
      collectsDataForRetraining: formData.collectsDataForRetraining?.toLowerCase() as YesNo,
      toolCTILink: formData.toolCTILink,
    });
  }

  // Include third-party specific details when specified
  if (includeThirdPartyDetails) {
    Object.assign(newTool, {
      otherPlatform: formData.platformName,
      vendorName: formData.vendorName,
      businessJustification: formData.businessJustification,
    });
  }

  if (voteTO) {
    clearTimeout(voteTO);
  }

  voteTO = setTimeout(async () => {
    try {
      const csrfToken = await csrfService.getToken();
      const response = await client.editTool(newTool, csrfToken);

      if ("errorMessage" in response) {
        callbacks.addErrorBanner(response.errorMessage, response.cti ?? true, response.error);
      } else {
        callbacks.navigateToSuccess();
        callbacks.addMessageBanner(getString("aiForMe.editToolBanner"), MessageBannerType.Success);
      }

      scrollTo({ top: 0 });
      callbacks.setIsLoading(false);
    } catch (reason: any) {
      callbacks.addErrorBanner(getString("aiForMe.failedTool2"));
      console.error(reason);
      callbacks.setIsLoading(false);
    }
  }, HANDLER_DEBOUNCE_TIMEOUT);
};
